import { computed, reactive, ref, shallowRef, watch } from 'vue'

import { useIntervalFn } from '@vueuse/core'
import { defineStore } from 'pinia'

import { isConflictError } from '@/apiClient'
import { ChatApi } from '@/apiClient/modules/chat'
import { FilesApi } from '@/apiClient/modules/files'
import {
  isUsageDailyDateOutdated,
  withUsageDailyDayFailed,
  withUsageDailyDays,
} from '@/apiClient/queries/usageDailyCacheUtils'
import { adminQueryKeys } from '@/apiClient/queries/useAdminQueries'
import type { IUploadedFile } from '@/apiClient/types/file'
import type { IUsageDailyMetricsResponse } from '@/apiClient/types/metrics'
import { useMetricEvents } from '@/composables/useMetricEvents'
import createLogger from '@/lib/logger'
import { queryClient } from '@/lib/queryClient'
import { refreshKeycloakToken } from '@/main'
import { useAuthStore } from '@/store/auth'
import type {
  DailyMetricsPayload,
  FileGenerationJobPayload,
  FileGenerationJobProgressPayload,
  FileProcessingPayload,
  HtmlAgentDraftEvent,
  HtmlAgentJobPayload,
  HtmlAgentReasoningEvent,
  ImageGenerationJobPayload,
  SocketEventNotification,
} from '@/store/socketEvents/types'
import {
  FILE_GENERATION_JOBS,
  fileGenerationJobEvent,
  type FileGenerationJobPhase,
  type FileGenerationJobType,
  withDeliveredJobFile,
} from '@/utils/fileGenerationJobs'
import {
  HTML_AGENT_CANCELLED_MESSAGE,
  HTML_AGENT_FAILURE_MESSAGE,
  HTML_AGENT_JOB_TYPE,
  HTML_AGENT_PROGRESS_STEP,
  HTML_AGENT_STALLED_MESSAGE,
  runningHtmlAgentJob,
} from '@/utils/htmlAgentJob'

import { useChatStore } from '../chat'
import { useFileStore } from '../file'
import { useToast } from '../toast'

const logger = createLogger('websockets')

/** Debounced reconnect delay for 1003 / 1012 / 1008 after refresh (ms). */
const SOCKET_RECONNECT_DEBOUNCE_MS = 1000

/** 1013 (Try Again Later): exponential backoff base and cap (ms). */
const SOCKET_1013_BACKOFF_BASE_MS = 2000
const SOCKET_1013_BACKOFF_MAX_MS = 60_000

/** How often running page jobs are checked, in case the socket missed their end (ms). */
const HTML_AGENT_JOB_POLL_INTERVAL_MS = 15_000
/** How long the poll waits on a page job before the row stops waiting (ms). */
const HTML_AGENT_JOB_POLL_LIMIT_MS = 15 * 60_000

/** A reply's page job the poll is waiting on. */
interface IPolledHtmlAgentJob {
  chatId: string
  messageId: string
  jobId: string
}

export const useSocketEventsStore = defineStore('sockerEventStore', () => {
  const authStore = useAuthStore()

  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** Increments on each 1013 close; reset on successful `onopen`. */
  let socket1013BackoffAttempt = 0

  /** Websocket status tracker */
  const socketStatus = reactive({
    /** Tracks if the socket has been initialised */
    isInitialised: ref(false),
    /** Tracks if the socket has been authenticated */
    isAuthenticated: ref(false),
    /** Tracks if the socket is connected */
    isConnected: ref(false),
  })

  /** Current socket connection */
  const socket = shallowRef<WebSocket>()

  /** Send a message to the socket */
  const sendMessage = (payload: Record<string, unknown>) => {
    if (!socket.value) {
      logger.error('socket not initialised')
      return
    }
    socket.value.send(JSON.stringify(payload))
  }

  /**
   * Parse the message payload from a MessageEvent into a JSON value.
   * @throws {TypeError} If message data is not a string
   * @throws {SyntaxError} If JSON parsing fails
   */
  const parseMessagePayload = <T = unknown>(event: MessageEvent): T => {
    const raw = event.data
    if (typeof raw !== 'string') {
      throw new TypeError('WebSocket message must be text to parse as JSON')
    }
    try {
      return JSON.parse(raw) as T
    } catch (cause: unknown) {
      throw new SyntaxError('Failed to parse WebSocket message as JSON', {
        cause,
      })
    }
  }

  /** Apply a file-processing lifecycle update to the matching uploaded file (by file_id). */
  const handleFileProcessingUpdate = (payload: FileProcessingPayload) => {
    logger.log('File processing notification payload', payload)
    useFileStore().updateUploadedFile(payload.file_id, {
      status: payload.status,
      error: payload.error ?? undefined,
      name: payload.filename,
    })
    // Processing can drop `url` and set `chd_redacted`; the payload carries neither.
    if (payload.status === 'processed') {
      FilesApi.getFile(payload.file_id)
        .then(({ url, status_details }) =>
          useFileStore().updateUploadedFile(payload.file_id, {
            url,
            status_details,
          }),
        )
        .catch((error) =>
          logger.error('File refresh after processing failed', error),
        )
    }
  }

  type ScheduleReconnectOptions = {
    /** If true (e.g. close 1008 policy / auth), refresh Keycloak before reconnecting. */
    refreshToken?: boolean
    /** Delay before reconnect; defaults to {@link SOCKET_RECONNECT_DEBOUNCE_MS}. */
    delayMs?: number
    /** Log line for observability (close code context). */
    logContext: string
  }

  /**
   * Drop the closed socket and open a new connection after `delayMs`.
   */
  const scheduleSocketReconnect = ({
    refreshToken = false,
    delayMs = SOCKET_RECONNECT_DEBOUNCE_MS,
    logContext,
  }: ScheduleReconnectOptions) => {
    socket.value = undefined
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer)
    }
    logger.log(logContext)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void (async () => {
        if (refreshToken) {
          try {
            logger.log('Refreshing keycloak token and reconnecting socket')
            await refreshKeycloakToken()
          } catch {
            logger.log(
              'WebSocket reconnect skipped after token refresh failure',
            )
            return
          }
        }
        openSocket()
      })()
    }, delayMs)
  }

  /** Open a new socket connection */
  const openSocket = () => {
    socket.value = new WebSocket(import.meta.env.VITE_API_SOCKET_ENDPOINT)

    socket.value.onopen = () => {
      socketStatus.isConnected = true
      socket1013BackoffAttempt = 0
      sendMessage({ token: authStore.accessToken })
      armHtmlAgentJobPoll()
      // A page job may have ended while the socket was down.
      pollHtmlAgentJobs()
    }

    socket.value.onclose = (event) => {
      socketStatus.isConnected = false
      logger.log('Socket disconnected', event.code, event)

      // Server close codes we handle explicitly (see RFC 6455 / MDN CloseEvent.code).
      if (event.code === 1003) {
        // Unsupported / invalid first payload (e.g. not JSON object) — retry same flow.
        scheduleSocketReconnect({
          logContext:
            'Socket close 1003 (unsupported data) — debounced reconnect',
        })
      } else if (event.code === 1006) {
        // Abnormal closure: no close frame (network, proxy, sleep, TLS drop).
        // Not an auth signal — avoid refreshKeycloakToken() here (can trigger login / loops).
        scheduleSocketReconnect({
          logContext:
            'Socket close 1006 (abnormal closure) — debounced reconnect',
        })
      } else if (event.code === 1008) {
        // Policy violation — treat as auth/token failure per server contract.
        scheduleSocketReconnect({
          refreshToken: true,
          logContext:
            'Socket close 1008 (policy violation) — refresh token then reconnect',
        })
      } else if (event.code === 1012) {
        // Service restart — server going away temporarily.
        scheduleSocketReconnect({
          logContext:
            'Socket close 1012 (service restart) — debounced reconnect',
        })
      } else if (event.code === 1013) {
        // Try again later — e.g. Redis temporarily unavailable (server contract).
        const delayMs = Math.min(
          SOCKET_1013_BACKOFF_MAX_MS,
          SOCKET_1013_BACKOFF_BASE_MS * 2 ** socket1013BackoffAttempt,
        )
        socket1013BackoffAttempt += 1
        scheduleSocketReconnect({
          delayMs,
          logContext: `Socket close 1013 (try again later) — reconnect in ${delayMs}ms (backoff attempt ${socket1013BackoffAttempt})`,
        })
      }
    }

    socket.value.onerror = (event) => {
      logger.error('Socket error:', event)
    }

    socket.value.onmessage = (event) => {
      logger.log('Socket message received', event)

      const parsed = parseMessagePayload(event)

      const top = parsed as { event?: unknown }
      if (top.event === 'html_draft') {
        handleHtmlAgentDraft(parsed as HtmlAgentDraftEvent)
        return
      }
      if (top.event === 'html_reasoning') {
        handleHtmlAgentReasoning(parsed as HtmlAgentReasoningEvent)
        return
      }
      if (top.event !== 'notification') {
        logger.log(`Unhandled event: ${String(top.event ?? 'unknown')}`)
        return
      }

      const socketNotification = parsed as SocketEventNotification<unknown>
      const notificationObj = socketNotification.notification

      logger.log('Notification object', notificationObj)

      const fileGenerationEvent = fileGenerationJobEvent(
        notificationObj?.type ?? '',
      )
      if (fileGenerationEvent) {
        handleFileGenerationJobNotification(
          fileGenerationEvent.jobType,
          fileGenerationEvent.phase,
          notificationObj.payload,
        )
        return
      }

      switch (notificationObj?.type) {
        case 'file_processing_started':
        case 'file_processing_succeeded':
        case 'file_processing_failed':
        case 'file_processing_retrying':
        case 'file_processing_verifying':
          handleFileProcessingUpdate(
            notificationObj.payload as FileProcessingPayload,
          )
          break

        case 'image_generation_job_succeeded':
          handleImageGenerationJobSucceeded(
            notificationObj.payload as ImageGenerationJobPayload,
          )
          break
        case 'image_generation_job_failed':
          handleImageGenerationJobFailed(
            notificationObj.payload as ImageGenerationJobPayload,
          )
          break

        case 'html_job_progress':
          handleFileGenerationJobProgress(
            notificationObj.payload as FileGenerationJobProgressPayload,
          )
          break
        case 'html_generation_job_succeeded':
          void handleHtmlAgentJobSucceeded(
            notificationObj.payload as HtmlAgentJobPayload,
          )
          break
        case 'html_generation_job_failed':
          handleHtmlAgentJobEnded(
            notificationObj.payload as HtmlAgentJobPayload,
            HTML_AGENT_FAILURE_MESSAGE,
          )
          break
        case 'html_generation_job_cancelled':
          handleHtmlAgentJobEnded(
            notificationObj.payload as HtmlAgentJobPayload,
            HTML_AGENT_CANCELLED_MESSAGE,
          )
          break
        case 'daily_metrics_computed':
          handleDailyMetricsComputed(
            notificationObj.payload as DailyMetricsPayload,
          )
          break

        case 'daily_metrics_failed':
          handleDailyMetricsFailed(
            notificationObj.payload as DailyMetricsPayload,
          )
          break

        default:
          logger.log(
            `Unhandled event: ${String(notificationObj?.type ?? 'unknown')}`,
          )
      }
    }
  }

  /** Initialise the socket events */
  const initSocketEvents = () => {
    logger.log('initSocketEvents')
    if (!import.meta.env.VITE_API_SOCKET_ENDPOINT) {
      logger.log('No socket endpoint configured')
      return
    }
    if (socket.value) {
      logger.log('socket already initialised')
      return
    }

    openSocket()
  }

  /** The files an assistant reply shows now, as the chat store holds them. */
  const replyFiles = (chatId: string, messageId: string) =>
    useChatStore()
      .getChatById(chatId)
      ?.messages.find((message) => message.id === messageId)?.files

  // TODO: find a better place for this:
  const handleImageGenerationJobSucceeded = (
    payload: ImageGenerationJobPayload,
  ) => {
    logger.log('Image generation job succeeded', payload)
    // No URL means nothing can render: the file would still retire the job and
    // pass the `image/` content-type check, leaving a shimmer with no job behind
    // it. Bail the way the file-generation siblings do. (`||` not `??`: '' is as
    // useless.)
    if (!payload.url && !payload.signed_url) {
      logger.error('Image generation job succeeded without a URL', payload)
      useChatStore().clearImageGenerationJob(
        payload.chat_id,
        payload.message_id,
      )
      useToast().onError('Generated image could not be loaded')
      return
    }

    // The event carries real file metadata, same shape as the file-generation jobs.
    const file: IUploadedFile = {
      id: payload.file_id,
      name: payload.file_name || payload.file_id,
      content_type: payload.file_content_type || 'image/',
      size: payload.file_size ?? 0,
      status: 'processed', // Not needed
      url: payload.url,
      signed_url: payload.signed_url,
      error: undefined,
    }

    // Add file to the chat message, next to what the reply's other jobs delivered.
    useChatStore().setChatResponseMessage({
      chat_id: payload.chat_id,
      message_id: payload.message_id,
      content: '',
      model: 'image-generation', // Not needed, doesn't override
      timestamp: Date.now(),
      files: withDeliveredJobFile(
        replyFiles(payload.chat_id, payload.message_id),
        file,
      ),
    })
    useChatStore().clearImageGenerationJob(payload.chat_id, payload.message_id)
  }

  const handleImageGenerationJobFailed = (
    payload: ImageGenerationJobPayload,
  ) => {
    logger.log('Image generation job failed', payload)
    useChatStore().clearImageGenerationJob(payload.chat_id, payload.message_id)
    useToast().onError(payload.error || 'Image generation job failed')
  }

  /** Routes a `FILE_GENERATION_JOBS` websocket event to its phase handler. */
  const handleFileGenerationJobNotification = (
    jobType: FileGenerationJobType,
    phase: FileGenerationJobPhase,
    payload: unknown,
  ) => {
    switch (phase) {
      case 'progress':
        handleFileGenerationJobProgress(
          payload as FileGenerationJobProgressPayload,
        )
        break
      case 'succeeded':
        handleFileGenerationJobSucceeded(
          jobType,
          payload as FileGenerationJobPayload,
        )
        break
      case 'failed':
        handleFileGenerationJobFailed(
          jobType,
          payload as FileGenerationJobPayload,
        )
        break
    }
  }

  const handleFileGenerationJobProgress = (
    payload: FileGenerationJobProgressPayload,
  ) => {
    logger.log('File generation job progress', payload)
    if (!payload.ui_hints) return
    useChatStore().setChatResponseMessage({
      chat_id: payload.chat_id,
      message_id: payload.message_id,
      ui_hints: payload.ui_hints,
    })
  }

  const handleFileGenerationJobSucceeded = (
    jobType: FileGenerationJobType,
    payload: FileGenerationJobPayload,
  ) => {
    const { logLabel, generatedMetricEvent } = FILE_GENERATION_JOBS[jobType]
    logger.log(`${logLabel} job succeeded`, payload)
    if (!payload.file_id || !payload.file_name || !payload.file_content_type) {
      logger.error(
        `${logLabel} job succeeded but file metadata is missing`,
        payload,
      )
      useChatStore().clearFileGenerationPlaceholder(
        payload.chat_id,
        payload.message_id,
        jobType,
      )
      return
    }
    useMetricEvents().sendEvent(generatedMetricEvent)
    const file: IUploadedFile = {
      id: payload.file_id,
      name: payload.file_name,
      content_type: payload.file_content_type,
      size: payload.file_size ?? 0,
      status: 'processed',
      url: payload.url ?? undefined,
      signed_url: payload.signed_url ?? undefined,
    }
    // Leave the reply as a reload shows it: its text and the files its other jobs delivered
    // stay, this job's placeholder gives way to the file, and only this job's progress goes.
    const deliveryMessage = payload.delivery_message
    // An API that does not send the delivery message yet: its text goes on the reply.
    const contentReplace =
      !deliveryMessage &&
      typeof payload.content === 'string' &&
      payload.content.length > 0
        ? payload.content
        : undefined
    useChatStore().setChatResponseMessage({
      chat_id: payload.chat_id,
      message_id: payload.message_id,
      content_replace: contentReplace,
      files: withDeliveredJobFile(
        replyFiles(payload.chat_id, payload.message_id),
        file,
        jobType,
      ),
      clear_processing_step: FILE_GENERATION_JOBS[jobType].progressStep,
    })
    // The delivery text ("Your presentation is ready…") is a message of its own in the chat.
    if (deliveryMessage) {
      useChatStore().appendChatMessage(payload.chat_id, deliveryMessage)
    }
  }

  const handleFileGenerationJobFailed = (
    jobType: FileGenerationJobType,
    payload: FileGenerationJobPayload,
  ) => {
    const { logLabel, failureMessage } = FILE_GENERATION_JOBS[jobType]
    logger.log(`${logLabel} job failed`, payload)
    useChatStore().clearFileGenerationPlaceholder(
      payload.chat_id,
      payload.message_id,
      jobType,
    )
    useChatStore().markFileGenerationJobFailed(
      payload.chat_id,
      payload.message_id,
      jobType,
      payload.error,
    )
    useToast().onError(payload.error || failureMessage)
  }

  // #region HTML agent page jobs
  /**
   * Kept from the first one: the reply's card and the preview start from it. Drafts and job
   * events come over different channels, so a draft can land after its job ended.
   */
  const handleHtmlAgentDraft = (event: HtmlAgentDraftEvent) => {
    const chatStore = useChatStore()
    if (chatStore.isHtmlAgentJobEnded(event.job_id)) return
    if (!chatStore.getChatMessage(event.chat_id, event.message_id)) return
    chatStore.setHtmlAgentDraft(event.message_id, event.html)
  }

  /** Kept until the job ends; its progress step then holds the whole reasoning. */
  const handleHtmlAgentReasoning = (event: HtmlAgentReasoningEvent) => {
    const chatStore = useChatStore()
    if (chatStore.isHtmlAgentJobEnded(event.job_id)) return
    if (!chatStore.getChatMessage(event.chat_id, event.message_id)) return
    chatStore.setHtmlAgentReasoning(event.message_id, event.reasoning)
  }

  /** The job is over: its draft and live reasoning go, and late ones are ignored. */
  const endHtmlAgentJob = (payload: HtmlAgentJobPayload) => {
    const chatStore = useChatStore()
    chatStore.markHtmlAgentJobEnded(payload.job_id)
    if (payload.message_id) {
      chatStore.clearHtmlAgentDraft(payload.message_id)
      chatStore.clearHtmlAgentReasoning(payload.message_id)
    }
  }

  /**
   * The server rewrote the reply with the finished page. The event does not carry it, so the
   * reply's saved content is fetched, unless the chat is not loaded (opening it gets the page).
   */
  const handleHtmlAgentJobSucceeded = async (payload: HtmlAgentJobPayload) => {
    logger.log('HTML agent job succeeded', payload)
    const chatStore = useChatStore()
    // The websocket and the poll can both report it.
    if (chatStore.isHtmlAgentJobEnded(payload.job_id)) return
    // The preview keeps the last draft on screen until the page below replaces it.
    endHtmlAgentJob(payload)
    const { chat_id: chatId, message_id: messageId } = payload
    if (!messageId || !chatStore.getChatMessage(chatId, messageId)) return

    let isPageLoaded = true
    if (payload.content) {
      chatStore.setChatResponseMessage({
        chat_id: chatId,
        message_id: messageId,
        content_replace: payload.content,
      })
    } else {
      isPageLoaded = await chatStore
        .reloadAssistantMessageContent(chatId, messageId)
        .catch(() => false)
    }
    // The job's step stays: it lists the agent's actions under the page tool's row.
    chatStore.clearPendingJob(chatId, messageId, HTML_AGENT_JOB_TYPE)
    if (!isPageLoaded) {
      useToast().onError('Your page is ready. Reload the chat to see it.')
    }
  }

  /** Failed or cancelled: the tool row says why, and only a failure warrants a toast. */
  const handleHtmlAgentJobEnded = (
    payload: HtmlAgentJobPayload,
    title: string,
  ) => {
    logger.log('HTML agent job ended', payload)
    const chatStore = useChatStore()
    // The websocket, the poll and Stop can each report it.
    if (chatStore.isHtmlAgentJobEnded(payload.job_id)) return
    endHtmlAgentJob(payload)
    const { chat_id: chatId, message_id: messageId } = payload
    if (!messageId) return
    chatStore.clearPendingJob(chatId, messageId, HTML_AGENT_JOB_TYPE)
    chatStore.markJobProgressFailed(
      chatId,
      messageId,
      HTML_AGENT_PROGRESS_STEP,
      title,
      payload.error,
    )
    if (payload.status === 'failed') {
      useToast().onError(payload.error || HTML_AGENT_FAILURE_MESSAGE)
    }
  }

  /**
   * The composer's Stop while a reply's page builds. The cancel's answer ends the job at once,
   * and its websocket event is then ignored. A job that finished meanwhile is left to its own
   * event.
   */
  const stopHtmlAgentJob = async (
    chatId: string,
    messageId: string,
    jobId: string,
  ) => {
    try {
      const job = await ChatApi.cancelHtmlGenerationJob(chatId, jobId)
      if (job.status !== 'cancelled') return
      handleHtmlAgentJobEnded(
        { ...job, message_id: job.message_id ?? messageId },
        HTML_AGENT_CANCELLED_MESSAGE,
      )
    } catch (error) {
      if (isConflictError(error)) return
      useToast().onError('Could not stop the page. Try again.')
    }
  }
  // #endregion

  // #region HTML agent page job poll
  /** Page jobs the loaded chats' replies list as running. */
  const runningHtmlAgentJobs = computed(() =>
    Object.values(useChatStore().chatMapById).flatMap((chat) =>
      chat.messages.flatMap((message): IPolledHtmlAgentJob[] => {
        const job = runningHtmlAgentJob(message)
        return job && message.id
          ? [{ chatId: chat.id, messageId: message.id, jobId: job.job_id }]
          : []
      }),
    ),
  )

  /** When the poll first saw each page job, for {@link HTML_AGENT_JOB_POLL_LIMIT_MS}. */
  const htmlAgentJobFirstPolledAt = new Map<string, number>()

  /**
   * Running far longer than a build takes: the server may have lost the job (it restarted
   * mid-build), so the row stops waiting. The job is not marked ended, so a late end still lands.
   */
  const stopWaitingOnHtmlAgentJob = ({
    chatId,
    messageId,
  }: IPolledHtmlAgentJob) => {
    const chatStore = useChatStore()
    chatStore.clearPendingJob(chatId, messageId, HTML_AGENT_JOB_TYPE)
    chatStore.markJobProgressFailed(
      chatId,
      messageId,
      HTML_AGENT_PROGRESS_STEP,
      HTML_AGENT_STALLED_MESSAGE,
      'Reload the chat to check on it.',
    )
    useToast().onWarning(
      'Your page is taking longer than expected. Reload the chat to check on it.',
    )
  }

  /** Asks after one page job; an end it answers goes through the websocket's handlers. */
  const pollHtmlAgentJob = async (polledJob: IPolledHtmlAgentJob) => {
    const firstPolledAt =
      htmlAgentJobFirstPolledAt.get(polledJob.jobId) ?? Date.now()
    htmlAgentJobFirstPolledAt.set(polledJob.jobId, firstPolledAt)
    if (Date.now() - firstPolledAt > HTML_AGENT_JOB_POLL_LIMIT_MS) {
      stopWaitingOnHtmlAgentJob(polledJob)
      return
    }

    const job = await ChatApi.getHtmlGenerationJob(
      polledJob.chatId,
      polledJob.jobId,
    ).catch((error: unknown) => {
      // The next poll asks again.
      logger.error('HTML agent job poll failed', polledJob.jobId, error)
      return undefined
    })
    if (!job || job.status === 'pending' || job.status === 'running') return

    const payload = {
      ...job,
      message_id: job.message_id ?? polledJob.messageId,
    }
    if (payload.status === 'succeeded') {
      void handleHtmlAgentJobSucceeded(payload)
    } else {
      handleHtmlAgentJobEnded(
        payload,
        payload.status === 'failed'
          ? HTML_AGENT_FAILURE_MESSAGE
          : HTML_AGENT_CANCELLED_MESSAGE,
      )
    }
  }

  const pollHtmlAgentJobs = () => {
    for (const job of runningHtmlAgentJobs.value) void pollHtmlAgentJob(job)
  }

  const htmlAgentJobPoll = useIntervalFn(
    pollHtmlAgentJobs,
    HTML_AGENT_JOB_POLL_INTERVAL_MS,
    { immediate: false },
  )

  /**
   * Polls only while some reply's page job runs. Armed when the socket first opens, so the
   * store can be created without the chat store.
   */
  let isHtmlAgentJobPollArmed = false
  const armHtmlAgentJobPoll = () => {
    if (isHtmlAgentJobPollArmed) return
    isHtmlAgentJobPollArmed = true
    watch(
      () => runningHtmlAgentJobs.value.length > 0,
      (hasRunningJobs) => {
        if (hasRunningJobs) htmlAgentJobPoll.resume()
        else htmlAgentJobPoll.pause()
      },
      { immediate: true },
    )
  }
  // #endregion

  // #region Daily usage metrics
  /** Cached usage-daily responses in the payload's scope (all users, or one employee). */
  const usageDailyQueriesFor = (employeeId: string | undefined) =>
    queryClient
      .getQueriesData<IUsageDailyMetricsResponse>({
        queryKey: [...adminQueryKeys.all, 'usageDaily'],
      })
      .flatMap(([queryKey, data]) =>
        data && (data.employee_id ?? undefined) === employeeId
          ? [{ queryKey, data }]
          : [],
      )

  /** When each cached usage-daily query last refetched for a date, by query and date. */
  const usageDailyRefetchedAt = new Map<string, number>()

  const handleDailyMetricsComputed = (payload: DailyMetricsPayload) => {
    for (const { queryKey, data } of usageDailyQueriesFor(
      payload.employee_id,
    )) {
      // Employee-scoped days are not cached server-side, so a refetch would compute again.
      if (payload.day_data) {
        queryClient.setQueryData(
          queryKey,
          withUsageDailyDays(data, [payload.day_data]),
        )
      } else {
        const refetchKey = `${JSON.stringify(queryKey)}:${payload.bucket_date}`
        if (
          isUsageDailyDateOutdated(
            data,
            payload.bucket_date,
            usageDailyRefetchedAt.get(refetchKey),
          )
        ) {
          usageDailyRefetchedAt.set(refetchKey, Date.now())
          queryClient.invalidateQueries({ queryKey, exact: true })
        }
      }
    }
  }

  const handleDailyMetricsFailed = (payload: DailyMetricsPayload) => {
    logger.log('Daily metrics computation failed', payload)
    for (const { queryKey, data } of usageDailyQueriesFor(
      payload.employee_id,
    )) {
      queryClient.setQueryData(
        queryKey,
        withUsageDailyDayFailed(data, payload.bucket_date),
      )
    }
  }
  // #endregion

  return {
    socket,
    initSocketEvents,
    stopHtmlAgentJob,
  }
})
